/* Copyright (c) 2021-2026, NVIDIA CORPORATION.  All rights reserved.
 *
 * NVIDIA CORPORATION and its licensors retain all intellectual property
 * and proprietary rights in and to this software, related documentation
 * and any modifications thereto.  Any use, reproduction, disclosure or
 * distribution of this software and related documentation without an express
 * license agreement from NVIDIA CORPORATION is strictly prohibited.
 */

import { Component, OnInit, OnDestroy } from '@angular/core';
import { Router } from '@angular/router';
import { takeUntil } from 'rxjs/operators';
import { Subject } from 'rxjs';

import { MatDialog } from '@angular/material/dialog';

import { LocaleService, BackgroundColorService } from 'crimson';
import { PlaytestLiveService } from './core/services/playtest-live.service';
import { PublicLearnArticleService } from './core/services/public-learn-article.service';
import { PlaytestState } from './shared';
import { AppBarLogoBehavior, AppBarService } from 'app-bar';

import { InvitationResultComponent } from './invitation-result/invitation-result.component';

@Component({
    selector: 'playtest-live-app-root',
    templateUrl: './app.component.html',
    styleUrls: ['./app.component.scss'],
    standalone: false
})
export class AppComponent implements OnInit, OnDestroy {
    private destroy$: Subject<boolean> = new Subject<boolean>();
    AppBarLogoBehavior = AppBarLogoBehavior;

    constructor(
        private playtestLiveService: PlaytestLiveService,
        private localeService: LocaleService,
        private appBarService: AppBarService,
        private router: Router,
        private dialog: MatDialog,
        private backgroundColorService: BackgroundColorService,
        private publicLearnArticleService: PublicLearnArticleService
    ) {
        this.publicLearnArticleService.init();
        this.playtestLiveService.init();
        this.backgroundColorService.init(true);

        this.appBarService.setTitle({ entry: 'GeForce NOW Playtest' });
        this.localeService.useDefaultLocale().pipe(takeUntil(this.destroy$)).subscribe();

        const urlQueryString = window.location.search;
        const urlParams = new URLSearchParams(urlQueryString);
        const verifyCode = urlParams.get('verify');
        if (verifyCode) {
            this.playtestLiveService.setPlaytesterVerifyCode(verifyCode);
        }
        this.playtestLiveService
            .getPageState()
            .pipe(takeUntil(this.destroy$))
            .subscribe(state => {
                switch (state) {
                    case PlaytestState.invitationRedeemed:
                        this.dialog.open(InvitationResultComponent, {
                            panelClass: ['nv-hig', 'nv-igo'],
                            disableClose: false,
                            hasBackdrop: true,
                            autoFocus: false
                        });
                        break;
                    case PlaytestState.login:
                        this.router.navigate(['/login'], { skipLocationChange: true });
                        break;
                    case PlaytestState.home:
                    case PlaytestState.settings:
                    case PlaytestState.invites:
                        this.router.navigate(['/home'], { skipLocationChange: true });
                        break;
                }
            });
    }

    ngOnInit() {
        this.appBarService.setTitle({ entry: 'GeForce NOW Playtest' });
    }

    ngOnDestroy(): void {
        this.destroy$.next(true);
        this.destroy$.unsubscribe();
    }
}
